// Currency / date / percentage formatting.
//
// Currency: arch §3 fixes a string-decimal wire format ("1500.00"). The
// frontend MUST NOT call parseFloat / Number on currency strings —
// IEEE-754 rounding silently corrupts the cents. We split on the decimal
// separator and use Intl.NumberFormat against the integer parts to keep
// a string-safe path.
//
// Dates: spec CN-05 + arch §3 — calendar dates are TZ-free; datetimes
// are wire-UTC and rendered in America/New_York at the presentation tier.

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatCurrencyString(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  // Wire is decimal string with exactly two decimals per api-design §1.
  // Defensive: handle a leading sign + tolerate missing decimal point.
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [intPart, fracPart = '00'] = unsigned.split('.');
  // Pad / truncate fractional to exactly 2 digits.
  const frac = (fracPart + '00').slice(0, 2);
  // Build a Number from intPart only (integer, safe at sane scales) and
  // format it with no decimals; then append the cents portion.
  // For very large totals (> 2^53 cents = ~$90 trillion) this drifts
  // — V1 sizes are SOW values, not national debt.
  const intNumeric = Number(intPart || '0');
  if (!Number.isFinite(intNumeric)) {
    return (negative ? '-' : '') + '$' + (intPart || '0') + '.' + frac;
  }
  const intFmt = USD.format(intNumeric).replace(/\.\d{2}$/, '');
  // intFmt looks like "$1,500"; append the cents.
  return (negative ? '−' : '') + intFmt + '.' + frac;
}

export function formatPercentInt(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${Math.trunc(n)}%`;
}

export function formatPercentOneDecimal(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${Math.round(n * 10) / 10}%`;
}

const CALENDAR = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const DATETIME_ET = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZone: 'America/New_York',
  timeZoneName: 'short',
});

export function formatCalendarDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  // Calendar date "2026-05-05" — render without TZ conversion. Building
  // a Date from the ISO date alone places it at UTC midnight; converting
  // to local time can shift by a day. We parse manually.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  // Construct a UTC date at noon to avoid any DST edge.
  const d = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  return CALENDAR.format(d).replace(/UTC|GMT/, '').trim();
}

export function formatDatetimeET(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return DATETIME_ET.format(d);
}

// Days-remaining renderer used by EndingSoonCard.
export function formatDaysRemaining(n: number): string {
  if (n < 0) {
    const abs = Math.abs(n);
    return `${abs} day${abs === 1 ? '' : 's'} overdue`;
  }
  if (n === 0) return 'Ends today';
  return `${n} day${n === 1 ? '' : 's'} remaining`;
}

// Status / type display labels.
export function formatStatus(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function formatType(t: string): string {
  if (t === 't_and_m') return 'T&M';
  if (t === 'fixed_price') return 'Fixed-Price';
  return t;
}

export function formatRole(r: string): string {
  if (r === 'admin') return 'Admin';
  if (r === 'practice_lead') return 'Practice Lead';
  if (r === 'member') return 'Member';
  return r;
}

export function formatCreditType(t: string): string {
  if (t === 'sales') return 'Sales';
  if (t === 'managed') return 'Managed';
  return t;
}

export function formatDerivedStatus(d: string): string {
  if (d === 'active') return 'Active';
  if (d === 'upcoming') return 'Upcoming';
  if (d === 'past') return 'Past';
  return d;
}
